import DashboardService from "@/src/api/DashboardService.js";
import {entityTypes} from "@/src/constants/entityTypeConstants.js";
import {renderEntity} from "@/src/utils/utils.js";
import {quickActionsTypes} from "@/src/constants/componentConstants.js";
import mqttService from "@/src/services/mqtt/mqttService.js";
import WorkspaceService from "@/src/api/WorkspaceService.js";

const ENVIRONMENT = import.meta.env?.VITE_ENV || process.env.NODE_ENV || 'development';

export const enrichWithApollo = async (
  id,
  type,
  reveal_emails = false,
  reveal_phone_numbers = false,
  includeRequestId = false,
) => {
  try {
    let response = null;
    if (type === entityTypes.CONTACT) {
      response = await DashboardService.enrichContactDetails(
        id,
        reveal_emails,
        reveal_phone_numbers,
      );
      if (response?.success) {
        return includeRequestId
          ? { data: response.contact, requestId: response.request_id }
          : response.contact;
      }
    }
    if (type === entityTypes.LEAD) {
      response = await DashboardService.enrichLeadDetails(
        id,
        reveal_emails,
        reveal_phone_numbers,
      );
      if (response?.success) {
        return includeRequestId
          ? { data: response.lead, requestId: response.request_id }
          : response.lead;
      }
    }
    
    if (response && !response.success) {
      const error = new Error(response.message || response.error || "Enrichment failed");
      error.response = { data: response };
      throw error;
    }
  } catch (error) {
    console.error("Error fetching Contact details:", error);
    throw error;
  }
};

// Async version with MQTT real-time updates
export const enrichWithWaterfallAsync = async (
  id,
  type,
  reveal_emails = false,
  reveal_phone_numbers = false,
  listId = null,
  onProgress = null,
) => {
  return new Promise(async (resolve, reject) => {
    try {
      const workspaceId = WorkspaceService.getCurrentWorkspaceId();
      const entityType = type === entityTypes.CONTACT ? 'contact' : 'lead';
      const topic = `${ENVIRONMENT}/enrichment/${entityType}/${workspaceId}/${id}`;
      
      let subscribedTopic = null;
      let timeoutId = null;
      
      // Helper to normalize MQTT payloads and resolve/reject accordingly
      const handleMqttPayload = (payload) => {
        // Ensure we have a plain object
        const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
        const event = data?.event || data?.type; // backwards compatibility

        // Surface progress to caller with a stable shape
        if (onProgress && event) {
          try {
            onProgress({ type: event, ...data });
          } catch (_) {}
        }

        if (event === 'enrichment_completed') {
          if (timeoutId) clearTimeout(timeoutId);
          if (subscribedTopic) mqttService.unsubscribe(subscribedTopic);
          const enriched = data?.enriched_data || data?.data?.[entityType] || data?.data;
          resolve(enriched || {});
          return true;
        }

        if (event === 'enrichment_failed') {
          if (timeoutId) clearTimeout(timeoutId);
          if (subscribedTopic) mqttService.unsubscribe(subscribedTopic);
          const error = new Error(data?.error_message || data?.error || 'Enrichment failed');
          error.response = { data };
          reject(error);
          return true;
        }
        return false;
      };

      const hasMqtt = mqttService.isClientConnected();
      if (hasMqtt) {
        subscribedTopic = await mqttService.subscribe(topic, (message) => {
          try { handleMqttPayload(message); } catch (e) { console.error(e); }
        });
        
        // Set timeout for MQTT response (60 seconds)
        timeoutId = setTimeout(() => {
          if (subscribedTopic) mqttService.unsubscribe(subscribedTopic);
          reject(new Error('Enrichment timeout - no response received'));
        }, 60000);
      }
      
      // Start async enrichment
      let response = null;
      if (type === entityTypes.CONTACT) {
        response = await DashboardService.enrichContactAsync(id, {
          reveal_emails,
          reveal_phone_numbers,
          list_id: listId,
        });
      } else if (type === entityTypes.LEAD) {
        response = await DashboardService.enrichLeadAsync(id, {
          reveal_emails,
          reveal_phone_numbers,
          list_id: listId,
        });
      }
      
      if (response?.success === false) {
        if (timeoutId) clearTimeout(timeoutId);
        if (subscribedTopic) mqttService.unsubscribe(subscribedTopic);
        const error = new Error(response?.message || response?.error || 'Enrichment failed');
        error.response = { data: response };
        reject(error);
        return;
      }
      
      const immediate = response?.lead || response?.contact;
      if (immediate) {
        if (timeoutId) clearTimeout(timeoutId);
        if (subscribedTopic) mqttService.unsubscribe(subscribedTopic);
        resolve(immediate);
        return;
      }
      
      // If MQTT is not connected, fall back to polling using request_id
      if (!hasMqtt) {
        const requestId = response?.async_request_id || response?.request_id;
        if (requestId) {
          // Poll status until complete or timeout
          const maxAttempts = 10; // ~60s with 3s delay
          let attempt = 0;
          const delay = (ms) => new Promise((r) => setTimeout(r, ms));

          while (attempt < maxAttempts) {
            try {
              const statusResp = await DashboardService.getWaterfallEnrichmentStatus(requestId);
              const ok = statusResp?.success !== false;
              const statusData = statusResp?.data || statusResp;
              const status = statusResp?.status || statusData?.status;

              if (ok && (status.toLowerCase() === 'completed')) {
                // Normalize a minimal enriched shape for caller
                const enriched = statusData?.enriched_data || statusData;
                resolve(enriched || {});
                return;
              }
              if (ok && (status === 'FAILED' || status === 'failed' || status === 'ERROR')) {
                reject(new Error(statusResp?.error || 'Enrichment failed'));
                return;
              }
            } catch (_) {
              // ignore and keep polling
            }
            attempt += 1;
            await delay(3000);
          }
          reject(new Error('Enrichment timeout - no response received'));
          return;
        }

        // No requestId and no immediate data
        resolve({});
      }
      
      // If we have async_request_id, we're using async flow
      // The MQTT subscription will handle the actual data when it arrives
      // If no async_request_id and no MQTT, the polling branch already handled it
    } catch (error) {
      console.error("Error in async enrichment:", error);
      reject(error);
    }
  });
};

export const createHandleRowUpdate = (
  entities,
  setEntities,
  showEmailCompose,
) => {
  return (row) => {
    if (!row?.id) return;

    setEntities(
      entities.value.map((entity) =>
        entity.id === row.id
          ? {
              ...entity,
              name: renderEntity({
                ...entity,
                first_name: entity?.first_name || row?.first_name,
                last_name: entity?.last_name || row?.last_name,
              }),
              profile_pic: entity?.profile_pic || row?.profile_pic,
              email: entity?.email || row?.email,
              phone_number: entity?.phone_number || row?.phone_number,
              current_role: entity?.current_role || row?.current_role,
              current_company: entity?.current_company || row?.current_company,
              company_website: entity?.company_website || row?.company_website,
              company_linkedin:
                entity?.company_linkedin || row?.company_linkedin,
            }
          : entity,
      ),
    );

    if (row?.action === quickActionsTypes.EMAIL) showEmailCompose(row);
  };
};
